/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CaseService } from 'app/cm/services/case-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class CaseQueueHistoryGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public caseId: string | null = null;

    protected queues: ReadonlyArray<CM.CaseQueueHistory> | undefined = undefined;

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 10;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        ...rest: any[]) {
        super(...rest);
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadCaseQueueHistory();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadCaseQueueHistory(): Promise<void> {
        if(!this.caseId) return;

        const includes: string[] = [
            'queue',
            'metadata.ended.byUser',
            'metadata.started.byUser'
        ];

        const fields: string[] = [
            'queue.description',
            'metadata.ended.byUser.description',
            'metadata.ended.timestamp',
            'metadata.started.byUser.description',
            'metadata.started.timestamp'
        ];

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadQueueHistoryByCase(this.caseId, options, includes, fields);
            this.queues = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
